import { Link, useLocation } from 'react-router-dom';
import { X, MessageSquare, FileText, FolderOpen, Users, Settings, ChevronLeft, ChevronRight, Upload, Shield, DatabaseZap, LucideIcon } from 'lucide-react';
import { useIntelligence } from '../../contexts';
import { useAuth } from '../../../contexts/AuthContext';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ isOpen, onClose, isCollapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation();
  const { isAdmin } = useIntelligence();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const navItems: NavItem[] = [
    { path: '/intelligence/chat', label: 'Ask Questions', icon: MessageSquare },
    { path: '/intelligence/protocols', label: 'Study Folders', icon: FolderOpen },
    { path: '/intelligence/documents', label: 'Documents', icon: FileText },
    { path: '/intelligence/staging', label: 'Upload Queue', icon: Upload, adminOnly: true },
    { path: '/intelligence/users', label: 'User Access', icon: Users, adminOnly: true },
    { path: '/intelligence/oversight', label: 'Chat Oversight', icon: Shield, superAdminOnly: true },
    { path: '/intelligence/registry-adoption', label: 'Registry Adoption', icon: DatabaseZap, superAdminOnly: true },
    { path: '/intelligence/settings', label: 'Settings', icon: Settings },
  ];

  const filteredItems = navItems.filter(item => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (item.adminOnly) return isAdmin;
    return true;
  });

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 flex-shrink-0
          bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700
          transform transition-all duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-16' : 'w-56'}
        `}
      >
        {/* Mobile close button - only show when expanded */}
        {!isCollapsed && (
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 lg:hidden">
            <span className="font-semibold text-gray-900 dark:text-gray-100">Menu</span>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Collapse toggle (desktop only) */}
        <div className={`hidden lg:flex items-center border-b border-gray-200 dark:border-gray-700 ${isCollapsed ? 'justify-center p-2' : 'justify-end p-2 pr-3'}`}>
          <button
            onClick={onToggleCollapse}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`${isCollapsed ? 'p-2' : 'p-3'} space-y-1`}>
          {filteredItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center rounded-lg text-sm font-medium min-h-[44px] transition-colors duration-150 lg:min-h-0
                  focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${isCollapsed ? 'justify-center px-2 lg:py-2' : 'gap-3 px-2.5 lg:py-1.5'}
                  ${active
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-100'
                  }
                `}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon size={18} className={`flex-shrink-0 ${active ? 'text-primary-600 dark:text-primary-400' : ''}`} />
                {!isCollapsed && <span className="min-w-0 truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Help section - only show when expanded */}
        {!isCollapsed && (
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 dark:border-gray-700">
            <div className="rounded-lg bg-primary-50 p-3 dark:bg-primary-900/20">
              <p className="text-sm font-medium text-primary-900 dark:text-primary-300">Need help?</p>
              <p className="text-xs text-primary-700 mt-1 dark:text-primary-400">
                Ask questions about protocols, SOPs, or regulatory guidance.
              </p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
